<template>
  <ChatHistory v-if="isChatAvailable && isHistoryPopupVisible" />
  <Chat
    v-if="canMountChat"
    v-show="isChatVisible"
    :aria-hidden="!isChatVisible || undefined"
    class="chat-component"
  />
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import store from "../../store/index";
import Chat from "./Chat.vue";
import ChatHistory from "./ChatHistory.vue";

// The profile/settings page is excluded by product requirement.
const settingsRouteNames = ["UserProfile"];

// Authentication, registration and session flows.
const authFlowRouteNames = [
  "Login",
  "Register",
  "NoAccount",
  "ResetPassword",
  "NewPassword",
  "Mfa",
  "MfaSetup",
  "OidcAuth",
  "OidcSignOut",
  "AuthByToken",
];

// Public and one-off token pages. They are reachable without the app shell and
// most of them hide the footer as well, so the assistant has no place there.
const standaloneRouteNames = [
  "PublicNewsDetails",
  "PublicSchemeDetails",
  "OnzePublicNewsList",
  "SalesForce",
  "SalesForceCallback",
  "SalesForceUnsubscribe",
  "RatingPage",
  "ReactivationSuggestionsPage",
  "DeactivationForm",
  "NewUserSuggestionPage",
  "ImpressumPage",
  "Mailing",
];

// Error and dead-end pages.
const errorRouteNames = ["Unauthorized", "ErrorPage", "PortalExpired"];

const excludedRouteNames = new Set([
  ...settingsRouteNames,
  ...authFlowRouteNames,
  ...standaloneRouteNames,
  ...errorRouteNames,
]);

const route = useRoute();

const usesAIAssistant = computed(() => {
  const authUser = store.state.auth.authUser;

  return !!(authUser.userUsesAIAssistant || authUser.usesAIAssistant);
});

const isHistoryPopupVisible = computed(
  () => store.state.chat.isHistoryPopupVisible,
);

// The chat's start-up requests (AIController, "Front office access") need content access and a completed MFA step, and
// a 403 logs the user out (main.js). So the chat is mounted only for users with content access, and never on the
// authentication flow routes, where the session can still be waiting for the MFA code.
const hasContentAccess = computed(() => !!store.state.auth.authUser.hasContent);

const isAuthFlowRoute = computed(
  () =>
    // "/" is the unnamed login route.
    route.path === "/" || authFlowRouteNames.includes(route.name),
);

// Mounted with v-if, but hidden with v-show on the other excluded routes, so the conversation survives a visit to them.
const canMountChat = computed(
  () =>
    usesAIAssistant.value && hasContentAccess.value && !isAuthFlowRoute.value,
);

const isRouteAllowed = computed(
  () =>
    // "/" is the unnamed login route.
    route.path !== "/" && !excludedRouteNames.has(route.name),
);

const isChatAvailable = computed(
  () => canMountChat.value && isRouteAllowed.value,
);

const isChatVisible = computed(
  () => isRouteAllowed.value && !isHistoryPopupVisible.value,
);
</script>
